import { useState } from 'react'
import { X, Trash2, Loader2, Circle, PlayCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { EstadoTarea } from '@/types/database'

interface TareasBulkActionsBarProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onClearSelection: () => void
  onDeleteSelected?: () => Promise<void>
  onChangeEstado?: (estado: EstadoTarea) => Promise<void>
  isDeleting?: boolean
  isChangingEstado?: boolean
  className?: string
}

const ESTADO_CONFIG: Record<EstadoTarea, { label: string; icon: typeof Circle; color: string }> = {
  pendiente: { label: 'Pendiente', icon: Circle, color: 'text-slate-500' },
  en_proceso: { label: 'En Proceso', icon: PlayCircle, color: 'text-blue-500' },
  completada: { label: 'Completada', icon: CheckCircle2, color: 'text-green-500' },
}

export function TareasBulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  onChangeEstado,
  isDeleting = false,
  isChangingEstado = false,
  className,
}: TareasBulkActionsBarProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  if (selectedCount === 0) return null

  const allSelected = selectedCount === totalCount && totalCount > 0
  const isLoading = isDeleting || isChangingEstado

  const handleDelete = async () => {
    setShowDeleteDialog(false)
    await onDeleteSelected?.()
  }

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between gap-4 p-3 bg-accent/10 border border-accent rounded-lg animate-in slide-in-from-top-2 duration-200',
          className
        )}
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClearSelection}
            title="Cancelar selección"
          >
            <X className="h-4 w-4" />
          </Button>

          <span className="text-sm font-medium">
            {selectedCount} {selectedCount === 1 ? 'tarea seleccionada' : 'tareas seleccionadas'}
          </span>

          {!allSelected && (
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onSelectAll}>
              Seleccionar todas ({totalCount})
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onChangeEstado && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                >
                  {isChangingEstado ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Circle className="h-4 w-4 mr-2" />
                  )}
                  Cambiar estado
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(Object.keys(ESTADO_CONFIG) as EstadoTarea[]).map((estado) => {
                  const config = ESTADO_CONFIG[estado]
                  const Icon = config.icon
                  return (
                    <DropdownMenuItem
                      key={estado}
                      onClick={() => onChangeEstado(estado)}
                      disabled={isLoading}
                    >
                      <Icon className={cn('mr-2 h-4 w-4', config.color)} />
                      {config.label}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {onDeleteSelected && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isLoading}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Eliminar
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar tareas</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar {selectedCount}{' '}
              {selectedCount === 1 ? 'tarea' : 'tareas'}? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar {selectedCount} {selectedCount === 1 ? 'tarea' : 'tareas'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
